import loadable from '@loadable/component';

import { Grid } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { useFlag } from '@/features/unleash';

import { BriefModalOnLoad } from '@/components/BriefModal';
import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';
import { AutoAddCompanyToWatchlistBanner } from '@/components/AutoAddCompanyToWatchlist';
import BrazeContentCard from '@/components/BrazeContentCard';

import type { Watchlist } from '@/hooks/useWatchlist';
import { useSortedWatchlistItems } from '../../hooks/useWatchlistSorting';
import { usePanelWatchlistItemId } from '../../hooks/usePanelWatchlistItemId';
import { useIsMobileScreen } from '@/hooks/useMedia';

import { EventsModal } from '../EventsModal';
import WatchlistTable from '../WatchlistTable';
import DeprecatedWatchlistTable from '../DeprecatedWatchlistTable';
import { TableTitle } from '../DeprecatedWatchlistTable';

import { BrazeContentCardWrapper } from './styled';

const WatchlistPanel = loadable(() => import('../WatchlistPanel'));

const Header = loadable(() => import('../Header'), {
  resolveComponent: (c) => c.Header,
});

const Footer = loadable(() => import('../Footer'), {
  resolveComponent: (c) => c.Footer,
});

const TableActions = loadable(() => import('../WatchlistTable'), {
  resolveComponent: (c) => c.TableActions,
});

const NarrativesBanner = loadable(() => import('../NarrativesBanner'), {
  resolveComponent: (c) => c.NarrativesBanner,
});

const NarrativesFeedback = loadable(() => import('../NarrativesFeedback'), {
  resolveComponent: (c) => c.NarrativesFeedback,
});

const FloatingAddToWatchlist = loadable(
  () => import('../FloatingAddToWatchlist'),
  {
    resolveComponent: (c) => c.FloatingAddToWatchlist,
  }
);

export const PopulatedState = ({ watchlist }: { watchlist: Watchlist }) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const { flagsReady, enabled, variant } = useFlag({
    name: 'mono-watchlist-new-layout',
  });
  const showWatchlistNewLayout =
    flagsReady && enabled && variant.name === 'variant';

  const sortedWatchlistItems = useSortedWatchlistItems(watchlist?.items);
  const defaultWatchlistItemId = sortedWatchlistItems[0]?.id ?? null;
  const [panelWatchlistItemId, setPanelWatchlistItemId] =
    usePanelWatchlistItemId({
      defaultId: defaultWatchlistItemId,
    });

  return (
    <>
      <MiniPriceChartDefinitions />
      <BrazeContentCardWrapper>
        <BrazeContentCard
          feedType="watchlist.top"
          anchorTarget="_blank"
          className="px-x2 pb-x1 lg:px-0"
        />
      </BrazeContentCardWrapper>
      <Header watchlist={watchlist} />
      {isMobileScreen && <FloatingAddToWatchlist watchlist={watchlist} />}
      {isMobileScreen ? (
        <>
          <Grid
            templateColumns="auto auto"
            justifyContent="space-between"
            padding="x2"
          >
            <TableTitle watchlistItemCount={watchlist.items.length} />
            <TableActions
              onToggleTableExpansion={() => {
                setPanelWatchlistItemId((id) =>
                  id ? null : defaultWatchlistItemId
                );
              }}
            />
          </Grid>
          {showWatchlistNewLayout ? (
            <NarrativesBanner />
          ) : (
            <AutoAddCompanyToWatchlistBanner />
          )}
          {showWatchlistNewLayout ? (
            <WatchlistTable
              watchlistItemId={panelWatchlistItemId}
              onWatchlistItemSelect={setPanelWatchlistItemId}
            />
          ) : (
            <DeprecatedWatchlistTable
              watchlistItemId={panelWatchlistItemId}
              onWatchlistItemSelect={setPanelWatchlistItemId}
            />
          )}
          <Footer watchlist={watchlist} />
          {showWatchlistNewLayout && <NarrativesFeedback />}
          {panelWatchlistItemId ? (
            <WatchlistPanel
              watchlistItemId={panelWatchlistItemId}
              setWatchlistItemId={setPanelWatchlistItemId}
            />
          ) : null}
          <EventsModal />
          <BriefModalOnLoad />
        </>
      ) : (
        <Grid
          templateColumnsLg={panelWatchlistItemId ? 'auto 32%' : 'auto'}
          gap="16px"
        >
          <div>
            <Grid
              templateColumns="auto auto"
              justifyContent="space-between"
              paddingY="x2"
            >
              <TableTitle watchlistItemCount={watchlist.items.length} />
              <TableActions
                onToggleTableExpansion={() => {
                  setPanelWatchlistItemId((id) =>
                    id ? null : defaultWatchlistItemId
                  );
                }}
              />
            </Grid>
            {showWatchlistNewLayout ? (
              <NarrativesBanner />
            ) : (
              <AutoAddCompanyToWatchlistBanner />
            )}
            {showWatchlistNewLayout ? (
              <WatchlistTable
                watchlistItemId={panelWatchlistItemId}
                onWatchlistItemSelect={setPanelWatchlistItemId}
              />
            ) : (
              <DeprecatedWatchlistTable
                watchlistItemId={panelWatchlistItemId}
                onWatchlistItemSelect={setPanelWatchlistItemId}
              />
            )}
            <Footer watchlist={watchlist} />
            {showWatchlistNewLayout && <NarrativesFeedback />}
          </div>
          {panelWatchlistItemId ? (
            <WatchlistPanel
              watchlistItemId={panelWatchlistItemId}
              setWatchlistItemId={setPanelWatchlistItemId}
            />
          ) : null}
          <EventsModal />
          <BriefModalOnLoad />
        </Grid>
      )}
    </>
  );
};
